import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import {
  ArrowRightStartOnRectangleIcon,
  ChevronUpDownIcon,
  CreditCardIcon,
  PuzzlePieceIcon,
  ShieldCheckIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'
import { signOut } from 'firebase/auth'
import { Link } from 'react-router'
import { useAuth } from '../../providers/auth/useAuth'
import { useChat } from '../../providers/chat/context'
import { firstNameFromEmail } from '../../lib/emailName'
import { auth } from '../../lib/firebase'

const PAGES = [
  { path: '/permissions', label: 'Permissions', icon: ShieldCheckIcon },
  { path: '/integrations', label: 'Integrations', icon: PuzzlePieceIcon },
  { path: '/plans', label: 'Plans & Top-ups', icon: SparklesIcon },
  { path: '/billing', label: 'Billing & invoices', icon: CreditCardIcon },
]

const ITEM =
  'flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-[13px] text-stone-700 transition-colors data-focus:bg-stone-100 data-focus:text-stone-950 md:py-2'

export default function AccountMenu() {
  const { user } = useAuth()
  const { balance } = useChat()
  const firstName = firstNameFromEmail(user?.email)

  return (
    <Menu>
      <MenuButton
        aria-label="Account menu"
        className="flex h-10 min-w-0 grow items-center overflow-hidden rounded-xl text-left whitespace-nowrap transition-colors hover:bg-black/5"
      >
        <span className="flex size-8 shrink-0 items-center justify-center">
          <span className="flex size-6 items-center justify-center rounded-full bg-purple-700 text-xs font-semibold text-white">
            {firstName.charAt(0)}
          </span>
        </span>
        <span className="min-w-0 grow leading-tight">
          <span className="block truncate text-sm text-stone-950">{firstName}</span>
          <span className="block truncate text-xs text-stone-500">{user?.email}</span>
        </span>
        <ChevronUpDownIcon className="mr-1.5 size-4 shrink-0 text-stone-400" />
      </MenuButton>

      <MenuItems
        anchor={{ to: 'top start', gap: 6 }}
        transition
        className="z-50 max-h-(--anchor-max-height) w-56 overflow-y-auto rounded-xl border border-stone-200 bg-white shadow-[0_2px_4px_rgba(28,25,23,0.04),0_18px_44px_-16px_rgba(28,25,23,0.22)] transition duration-100 ease-out data-closed:translate-y-1 data-closed:opacity-0"
      >
        <div className="border-b border-stone-100 px-4 py-3">
          <p className="text-[10px] font-medium tracking-[0.08em] text-stone-400 uppercase">Signed in as</p>
          <p className="mt-1 truncate text-sm text-stone-900">{user?.email}</p>

          {balance && (
            <>
              <div className="mt-3 flex items-baseline justify-between text-xs">
                <span className="font-medium text-stone-700">Credits</span>
                <span className="text-stone-500">
                  {balance.credits_used} / {balance.credits_total} credits used
                </span>
              </div>
              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-stone-100">
                <div
                  className="h-full rounded-full bg-purple-500 transition-[width]"
                  style={{ width: `${Math.min(100, (balance.credits_used / Math.max(balance.credits_total, 1)) * 100)}%` }}
                />
              </div>
            </>
          )}
        </div>

        <div className="p-1">
          {PAGES.map(({ path, label, icon: Icon }) => (
            <MenuItem key={path}>
              <Link to={path} className={ITEM}>
                <Icon className="size-4 text-stone-400" />
                {label}
              </Link>
            </MenuItem>
          ))}
        </div>

        <div className="border-t border-stone-100 p-1">
          <MenuItem>
            <button type="button" onClick={() => signOut(auth)} className={ITEM}>
              <ArrowRightStartOnRectangleIcon className="size-4 text-stone-400" />
              Log out
            </button>
          </MenuItem>
        </div>
      </MenuItems>
    </Menu>
  )
}
